import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ordersApi, shippingApi, productsApi, jobsApi, JobStatus } from '../services/api';
import { OrderDetailsModal } from '../components/OrderDetailsModal';
import { NewOrderModal } from '../components/NewOrderModal';
import { useToast } from '../components/Toast';
import { ArchiveBoxIcon, MapPinIcon, InboxIcon, FunnelIcon, PlusIcon } from '@heroicons/react/24/outline';
import { formatDate, formatTime } from '../utils/dateUtils';

interface Order {
  id: string;
  order_number: string;
  first_name?: string;
  last_name?: string;
  customer_email?: string;
  total_price: number;
  status: string;
  created_at: string;
  shipping_city?: string;
  shipping_state?: string;
  order_type?: string;
  pickup_ready?: boolean;
  pickup_ready_at?: string;
  pickup_completed_at?: string;
  is_blocked?: boolean;
  blocked_reason?: string;
}

export const Orders: React.FC = () => {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState<Order[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<{ status: string; progress?: number } | null>(null);
  const [batchShipping, setBatchShipping] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [initialTab, setInitialTab] = useState<'summary' | 'ship' | 'shipments' | 'history'>('ship');
  const [refreshing, setRefreshing] = useState(false); // silent background refresh to avoid table flash
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortField, setSortField] = useState<string>(() => 
    localStorage.getItem('orders_sort_field') || 'order_number'
  );
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => 
    (localStorage.getItem('orders_sort_direction') as 'asc' | 'desc') || 'desc'
  );
  const [page, setPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [deliveryFilter, setDeliveryFilter] = useState<'all' | 'shipping' | 'pickup'>('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    date_start: '',
    date_end: '',
    state: '',
    value_min: '',
    value_max: '',
  });
  const ordersPerPage = 50;
  const isLoading = loading && !refreshing;
  const [includeAllStatuses, setIncludeAllStatuses] = useState(false);
  const [showNewOrderModal, setShowNewOrderModal] = useState(false);

  const loadOrders = async (
    pageNum = page,
    search = searchQuery,
    delivery = deliveryFilter,
    filters = advancedFilters,
    options: { silent?: boolean } = {}
  ) => {
    const silent = options.silent === true;
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const offset = (pageNum - 1) * ordersPerPage;
      const params: any = {
        status: includeAllStatuses ? 'all' : 'awaiting_shipment',
        limit: ordersPerPage,
        offset: offset,
        sort_by: sortField,
        sort_dir: sortDirection,
        search: search || undefined,
      };
      console.log('[Orders] Loading with params:', { sort_by: sortField, sort_dir: sortDirection, page: pageNum });

      // Add delivery type filter if not 'all'
      if (delivery !== 'all') {
        params.order_type = delivery;
      }

      // Add advanced filters if set
      if (filters.date_start) params.date_start = filters.date_start;
      if (filters.date_end) params.date_end = filters.date_end;
      if (filters.state) params.state = filters.state;
      if (filters.value_min) params.value_min = filters.value_min;
      if (filters.value_max) params.value_max = filters.value_max;

      const data = await ordersApi.list(params);
      setOrders(data.orders || []);
      setTotalOrders(data.total || 0);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      if (silent) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadOrders(page, searchQuery, deliveryFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sortField, sortDirection, deliveryFilter, includeAllStatuses]);

  // Handle opening order from URL query param (e.g., from SLA breached modal)
  useEffect(() => {
    const selectedOrderId = searchParams.get('selected');
    if (selectedOrderId) {
      // Fetch and open the order (pass full response with order + items)
      ordersApi.get(selectedOrderId).then((data) => {
        setSelectedOrder(data);
        setInitialTab('summary');
        // Clear the query param after opening
        setSearchParams({});
      }).catch((error) => {
        console.error('Failed to load selected order:', error);
        toast.error('Failed to load order');
        setSearchParams({});
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Persist sort settings to localStorage
  useEffect(() => {
    localStorage.setItem('orders_sort_field', sortField);
    localStorage.setItem('orders_sort_direction', sortDirection);
  }, [sortField, sortDirection]);

  // Auto-refresh orders every 30 seconds to catch new webhook-created orders
  useEffect(() => {
    const autoRefreshInterval = setInterval(() => {
      // Only auto-refresh if not currently syncing or loading, and no modal is open
      if (!syncing && !loading && !refreshing && !selectedOrder) {
        console.log('[Orders] Auto-refresh: checking for new orders...');
        loadOrders(page, searchQuery, deliveryFilter, advancedFilters, { silent: true });
      }
    }, 30000); // 30 seconds

    return () => clearInterval(autoRefreshInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchQuery, deliveryFilter, advancedFilters, syncing, loading, refreshing, selectedOrder]);

  // Search with debouncing - only trigger when searchQuery changes (not on mount)
  const isFirstRender = React.useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      loadOrders(1, searchQuery);
      setPage(1);
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleSyncOrders = async () => {
    setSyncing(true);
    setSyncProgress({ status: 'Starting sync...' });
    
    try {
      // Try to use background job queue first
      const queueResult = await jobsApi.queueSync('full');
      
      if (queueResult.queued && queueResult.jobId) {
        // Background job is running - poll for progress
        setSyncProgress({ status: 'Syncing in background...', progress: 0 });
        
        const finalStatus = await jobsApi.pollUntilComplete(
          'sync',
          queueResult.jobId,
          (status: JobStatus) => {
            // Update progress as job runs
            if (status.state === 'active') {
              setSyncProgress({ 
                status: `Syncing... ${status.progress || 0}%`,
                progress: status.progress || 0
              });
            }
          },
          180000 // 3 minute timeout
        );

        if (finalStatus.state === 'completed') {
          const result = finalStatus.result || {};
          const productsInfo = result.products ? `Products: ${result.products.synced || 0} synced` : '';
          const ordersInfo = result.orders ? `Orders: ${result.orders.ordersAdded || 0} added, ${result.orders.ordersUpdated || 0} updated` : '';
          toast.success('Sync Complete', [productsInfo, ordersInfo].filter(Boolean).join('\n'));
        } else if (finalStatus.state === 'failed') {
          throw new Error(finalStatus.failedReason || 'Sync job failed');
        }
      } else {
        // Fall back to inline sync (job queue disabled)
        setSyncProgress({ status: 'Syncing products...' });
        
        try {
          await productsApi.sync();
        } catch (e) {
          console.log('Product sync skipped:', e);
        }

        setSyncProgress({ status: 'Updating order data...' });
        await ordersApi.updateProductData();

        setSyncProgress({ status: 'Syncing orders...' });
        const result = await ordersApi.sync();

        toast.success('Sync Complete', `${result.details.added + result.details.updated} orders synced\nAdded: ${result.details.added}\nUpdated: ${result.details.updated}`);
      }
      
      // Refresh orders list
      setPage(1);
      setOrders([]);
      await loadOrders(1, searchQuery, deliveryFilter, advancedFilters);
    } catch (error: any) {
      toast.error('Sync Failed', error.response?.data?.message || error.message);
    } finally {
      setSyncing(false);
      setSyncProgress(null);
    }
  };

  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleViewOrder = async (orderId: string, tab?: 'summary' | 'ship' | 'shipments' | 'history') => {
    try {
      const orderDetails = await ordersApi.get(orderId);
      console.log('[Orders] API Response from ordersApi.get():', orderDetails);
      console.log('[Orders] Items in response:', orderDetails?.items?.map((i: any) => ({
        title: i.title?.substring(0, 30),
        variant_weight: i.variant_weight,
        product_weight: i.product_weight,
        has_weight: !!(i.variant_weight || i.product_weight),
      })));
      setSelectedOrder(orderDetails);
      // Default to Ship tab for all orders (pickup workflow lives there)
      const defaultTab = tab ?? 'ship';
      setInitialTab(defaultTab);
    } catch (error) {
      console.error('Failed to load order details:', error);
      toast.error('Error', 'Failed to load order details');
    }
  };

  const handleShipOrder = () => {
    setSelectedOrder(null);
    // Reload orders to reflect status changes (shipped, cancelled, etc.)
    loadOrders(page, searchQuery, deliveryFilter, advancedFilters, { silent: true });
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc'); // Default to desc for new sorts
    }
    setPage(1); // Reset to first page when sorting
  };

  const handleApplyFilters = () => {
    setPage(1);
    loadOrders(1, searchQuery, deliveryFilter, advancedFilters);
  };

  const handleClearFilters = () => {
    setAdvancedFilters({
      date_start: '',
      date_end: '',
      state: '',
      value_min: '',
      value_max: '',
    });
    setPage(1);
    loadOrders(1, searchQuery, deliveryFilter, {
      date_start: '',
      date_end: '',
      state: '',
      value_min: '',
      value_max: '',
    });
  };

  const handleBatchShip = async () => {
    if (selectedOrders.length === 0) {
      toast.warning('No Selection', 'Please select orders to ship');
      return;
    }

    // Filter out pickup orders - only ship orders with order_type !== 'pickup'
    const shippingOrders = orders
      .filter(o => selectedOrders.includes(o.id) && o.order_type !== 'pickup')
      .map(o => o.id);

    if (shippingOrders.length === 0) {
      toast.warning('No Shipping Orders', 'No shipping orders selected. Pickup orders cannot be batch shipped.');
      return;
    }

    if (shippingOrders.length > 50) {
      toast.warning('Too Many Orders', 'Cannot process more than 50 orders at once. Please select fewer orders.');
      return;
    }

    const confirmMsg = `Create labels for ${shippingOrders.length} order${shippingOrders.length > 1 ? 's' : ''}?\n\nThis will:\n- Auto-calculate box sizes based on weights\n- Select the cheapest shipping rate for each order\n- Create labels immediately\n- Update order fulfillment status`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    setBatchShipping(true);
    setBatchProgress({ current: 0, total: shippingOrders.length });

    try {
      const result = await shippingApi.batchCreateLabels(shippingOrders, true);

      setBatchShipping(false);
      setBatchProgress({ current: 0, total: 0 });

      // Show results
      if (result.failed > 0) {
        let errorDetails = result.errors.slice(0, 5).map((err: any) => 
          `Order ${err.order_number || err.order_id}: ${err.error}`
        ).join('\n');
        if (result.errors.length > 5) {
          errorDetails += `\n... and ${result.errors.length - 5} more errors`;
        }
        toast.warning('Batch Shipping Complete', `${result.succeeded} succeeded, ${result.failed} failed\n\n${errorDetails}`);
      } else {
        toast.success('Batch Shipping Complete', `${result.succeeded} labels created successfully`);
      }

      // Download labels if any succeeded
      if (result.succeeded > 0) {
        // Open each label in a new tab for printing
        result.labels.forEach((label: any, index: number) => {
          setTimeout(() => {
            const pdfWindow = window.open('', `_blank_${index}`);
            if (pdfWindow) {
              pdfWindow.document.write(
                `<html><head><title>Label - ${label.tracking_number}</title></head><body style="margin:0">` +
                `<embed width="100%" height="100%" src="data:application/pdf;base64,${label.label_data}" type="application/pdf" />` +
                `</body></html>`
              );
            }
          }, index * 500); // Stagger opening to avoid browser blocking
        });

        // Auto-confirm all shipments after 2 seconds
        if (window.confirm('Labels created! Do you want to confirm all shipments and send tracking to Shopify?')) {
          for (const label of result.labels) {
            try {
              await shippingApi.confirmShipment(label.shipment_id);
            } catch (error) {
              console.error(`Failed to confirm shipment ${label.shipment_id}:`, error);
            }
          }
          toast.success('Shipments Confirmed', 'All shipments confirmed and sent to Shopify');
        }
      }

      // Reload orders and clear selection
      await loadOrders();
      setSelectedOrders([]);
    } catch (error: any) {
      setBatchShipping(false);
      setBatchProgress({ current: 0, total: 0 });
      toast.error('Batch Shipping Failed', error.response?.data?.error || error.message);
    }
  };

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden p-4 lg:p-6">
      {/* Filters & Actions - Compact on mobile */}
      <div className="flex-shrink-0 bg-white rounded-xl border border-slate-200 shadow-subtle p-3 lg:p-4 mb-4">
        {/* Mobile: Compact search + filter button */}
        <div className="flex gap-2 lg:hidden">
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
          />
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className={`px-3 py-2 rounded-lg border ${showMobileFilters ? 'bg-brand-blue text-white border-brand-blue' : 'bg-white text-slate-600 border-slate-200'}`}
          >
            <FunnelIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowNewOrderModal(true)}
            className="px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg"
          >
            <PlusIcon className="w-5 h-5" />
          </button>
          <button
            onClick={handleSyncOrders}
            disabled={syncing}
            className="px-3 py-2 text-sm font-medium text-white bg-brand-blue rounded-lg disabled:opacity-50"
          >
            {syncing ? '...' : 'Sync'}
          </button>
        </div>

        {/* Mobile: Expandable filters */}
        {showMobileFilters && (
          <div className="lg:hidden mt-3 pt-3 border-t border-slate-200 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <select className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
                <option>Awaiting</option>
                <option>Shipped</option>
                <option>Cancelled</option>
                <option>All</option>
              </select>
              <select
                value={deliveryFilter}
                onChange={(e) => {
                  setDeliveryFilter(e.target.value as 'all' | 'shipping' | 'pickup');
                  setPage(1);
                }}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                <option value="all">All Types</option>
                <option value="shipping">Ship</option>
                <option value="pickup">Pickup</option>
              </select>
            </div>
            {selectedOrders.length > 0 && (
              <button
                onClick={handleBatchShip}
                disabled={batchShipping}
                className="w-full px-3 py-2 text-sm font-medium text-white bg-brand-blue rounded-lg disabled:opacity-50"
              >
                {batchShipping ? `Shipping... ${batchProgress.current}/${batchProgress.total}` : `Batch Ship ${selectedOrders.length}`}
              </button>
            )}
          </div>
        )}

        {/* Desktop: Full filter bar */}
        <div className="hidden lg:flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <input
                type="text"
                placeholder="Search orders, customer..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue min-w-[200px]"
              />
              <button
                onClick={() => {
                  setIncludeAllStatuses((prev) => !prev);
                  setPage(1);
                }}
                className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                  includeAllStatuses
                    ? 'border-blue-200 bg-blue-50 text-blue-700'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
                title={includeAllStatuses ? 'Showing all orders' : 'Showing open orders'}
              >
                {includeAllStatuses ? 'Show Open Orders' : 'Show All Orders'}
              </button>
              <select
                value={deliveryFilter}
                onChange={(e) => {
                  setDeliveryFilter(e.target.value as 'all' | 'shipping' | 'pickup');
                  setPage(1);
                }}
                className="px-4 py-2 border border-slate-200 rounded-lg text-sm min-w-[170px]"
              >
                <option value="all">All Delivery Types</option>
                <option value="shipping">Ship Orders</option>
                <option value="pickup">Pickup Orders</option>
              </select>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg"
              >
                {showAdvancedFilters ? '▼' : '▶'} Advanced Filters
              </button>
              {selectedOrders.length > 0 && (
                <button
                  onClick={handleBatchShip}
                  disabled={batchShipping}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg disabled:opacity-50"
                >
                  {batchShipping
                    ? `Creating Labels... ${batchProgress.current}/${batchProgress.total}`
                    : `Batch Ship ${selectedOrders.length} Order${selectedOrders.length > 1 ? 's' : ''}`}
                </button>
              )}
              <button
                onClick={() => setShowNewOrderModal(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg flex items-center gap-1.5 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                New Order
              </button>
              <button
                onClick={handleSyncOrders}
                disabled={syncing}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg disabled:opacity-50"
              >
                {syncing ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {syncProgress?.status || 'Syncing...'}
                  </span>
                ) : 'Sync Orders'}
              </button>
            </div>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="pt-4 border-t border-slate-200 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Date Range */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={advancedFilters.date_start}
                  onChange={(e) => setAdvancedFilters({...advancedFilters, date_start: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={advancedFilters.date_end}
                  onChange={(e) => setAdvancedFilters({...advancedFilters, date_end: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                />
              </div>

              {/* State Filter */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Destination State</label>
                <input
                  type="text"
                  placeholder="e.g., GA, CA, NY"
                  value={advancedFilters.state}
                  onChange={(e) => setAdvancedFilters({...advancedFilters, state: e.target.value.toUpperCase()})}
                  maxLength={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                />
              </div>

              {/* Order Value Range */}
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Min Order Value ($)</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={advancedFilters.value_min}
                  onChange={(e) => setAdvancedFilters({...advancedFilters, value_min: e.target.value})}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">Max Order Value ($)</label>
                <input
                  type="number"
                  placeholder="1000.00"
                  value={advancedFilters.value_max}
                  onChange={(e) => setAdvancedFilters({...advancedFilters, value_max: e.target.value})}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                />
              </div>

              {/* Filter Actions */}
              <div className="flex items-end gap-2">
                <button
                  onClick={handleApplyFilters}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg transition-colors"
                >
                  Apply Filters
                </button>
                <button
                  onClick={handleClearFilters}
                  className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Order Cards - shown on mobile only */}
      <div className="lg:hidden flex-1 min-h-0 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] space-y-3 pb-[calc(env(safe-area-inset-bottom)+10rem)]">
        {isLoading && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-600">
            Loading orders...
          </div>
        )}
        {!isLoading && orders.map((order) => (
          <div
            key={order.id}
            onClick={() => handleViewOrder(order.id)}
            className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 active:bg-slate-50"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-mono font-semibold text-slate-900">
                    {/^[0-9]+$/.test(order.order_number) ? `#PSCS${order.order_number}` : order.order_number}
                  </p>
                  {order.is_blocked && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 border border-red-200"
                      title={order.blocked_reason || 'Matches blocklist'}
                    >
                      ⚠ BLOCKED
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-600">
                  {order.first_name} {order.last_name}
                </p>
                {order.shipping_city && order.shipping_state && (
                  <p className="text-xs text-slate-500">
                    {order.shipping_city}, {order.shipping_state}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="font-semibold text-slate-900">
                  ${order.total_price ? parseFloat(order.total_price.toString()).toFixed(2) : '0.00'}
                </p>
                <p className="text-xs text-slate-500">
                  {(order as any).total_quantity || 0} items
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex flex-wrap gap-2">
                {order.order_type === 'pickup' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                    <MapPinIcon className="w-3 h-3" />
                    Pickup
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                    Awaiting
                  </span>
                )}
              </div>
              {order.order_type === 'pickup' ? (
                <button
                  onClick={(e) => { e.stopPropagation(); handleViewOrder(order.id, 'ship'); }}
                  className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg"
                >
                  View
                </button>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); handleViewOrder(order.id, 'ship'); }}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-blue rounded-lg"
                >
                  Ship
                </button>
              )}
            </div>
          </div>
        ))}
        {!isLoading && orders.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
            <InboxIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-600">No orders found</p>
          </div>
        )}
        {/* Mobile Pagination */}
        {!isLoading && orders.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Page {page} of {Math.ceil(totalOrders / ordersPerPage)}
            </p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= Math.ceil(totalOrders / ordersPerPage)}
                className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
        <div className="h-16" />
      </div>

      {/* Orders Table */}
      <div className="flex-1 min-h-0 bg-white rounded-xl border border-slate-200 shadow-subtle overflow-hidden hidden lg:flex lg:flex-col">
        <div className="flex-1 overflow-auto">
          <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50 sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="px-6 py-3 text-left">
                <input
                  type="checkbox"
                  className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedOrders(orders.map((o) => o.id));
                    } else {
                      setSelectedOrders([]);
                    }
                  }}
                />
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:text-brand-blue"
                onClick={() => handleSort('order_number')}
              >
                Order {sortField === 'order_number' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:text-brand-blue"
                onClick={() => handleSort('first_name')}
              >
                Customer {sortField === 'first_name' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Items
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:text-brand-blue"
                onClick={() => handleSort('total_price')}
              >
                Total {sortField === 'total_price' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:text-brand-blue"
                onClick={() => handleSort('status')}
              >
                Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:text-brand-blue"
                onClick={() => handleSort('created_at')}
              >
                Date {sortField === 'created_at' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider sticky right-0 bg-slate-50 shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {isLoading && (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-slate-600">
                  Loading orders...
                </td>
              </tr>
            )}
            {!isLoading && orders.map((order) => (
              <tr
                key={order.id}
                onClick={() => handleViewOrder(order.id)}
                className="hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedOrders.includes(order.id)}
                    onChange={() => toggleOrderSelection(order.id)}
                    className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-medium text-slate-900">
                      {/^[0-9]+$/.test(order.order_number) ? `PSCS${order.order_number}` : order.order_number}
                    </span>
                    {order.is_blocked && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 border border-red-200"
                        title={order.blocked_reason || 'Matches blocklist'}
                      >
                        ⚠ BLOCKED
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <p className="text-sm text-slate-900">
                      {order.first_name} {order.last_name}
                    </p>
                    {order.shipping_city && order.shipping_state && (
                      <p className="text-xs text-slate-500">
                        {order.shipping_city}, {order.shipping_state}
                      </p>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-slate-600">
                    {(order as any).total_quantity || '-'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-medium text-slate-900">
                    ${order.total_price ? parseFloat(order.total_price.toString()).toFixed(2) : '0.00'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col gap-1">
                    {(() => {
                      const totalFulfilled = (order as any).total_fulfilled ?? 0;
                      const totalQty = (order as any).total_quantity ?? 0;
                      if (totalFulfilled > 0 && totalFulfilled < totalQty) {
                        return (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            <ArchiveBoxIcon className="w-3.5 h-3.5" />
                            Partially Shipped ({totalFulfilled}/{totalQty} items)
                          </span>
                        );
                      }

                      switch ((order.status || '').toLowerCase()) {
                        case 'awaiting_shipment':
                          return (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                              Awaiting Shipment
                            </span>
                          );
                        case 'shipped':
                          return (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              Shipped
                            </span>
                          );
                        case 'delivered':
                          return (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                              Delivered
                            </span>
                          );
                        case 'cancelled':
                        case 'canceled':
                          return (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                              Cancelled
                            </span>
                          );
                        default:
                          return (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                              {order.status || 'Unknown'}
                            </span>
                          );
                      }
                    })()}
                    {order.order_type === 'pickup' && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        <MapPinIcon className="w-3.5 h-3.5" />
                        Local Pickup
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                  <div>
                    <div>{formatDate(order.created_at)}</div>
                    <div className="text-xs text-slate-500">{formatTime(order.created_at)}</div>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-right text-sm sticky right-0 bg-white shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)]">
                  {order.order_type === 'pickup' ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewOrder(order.id, 'ship');
                      }}
                      className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md transition-colors min-w-[120px]"
                    >
                      View Pickup
                    </button>
                  ) : (
                    <>
                      {(() => {
                        const status = (order.status || '').toLowerCase();
                        const isShipped = status === 'shipped' || status === 'delivered';
                        return (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewOrder(order.id, isShipped ? 'shipments' : 'ship');
                            }}
                            className={`inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors min-w-[120px] ${
                              isShipped
                                ? 'text-white bg-emerald-600 hover:bg-emerald-700'
                                : 'text-white bg-brand-blue hover:bg-brand-blue-hover'
                            }`}
                          >
                            {isShipped ? 'View Details' : 'Ship Order'}
                          </button>
                        );
                      })()}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Empty State (when no orders) */}
        {!isLoading && orders.length === 0 && (
          <div className="text-center py-12">
            <InboxIcon className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            {searchQuery || deliveryFilter !== 'all' || advancedFilters.date_start || advancedFilters.state ? (
              <>
                <h3 className="text-lg font-medium text-slate-900 mb-2">No matching orders</h3>
                <p className="text-slate-600 mb-6">
                  Try adjusting your search or filters
                </p>
                <button 
                  onClick={() => {
                    setSearchQuery('');
                    setDeliveryFilter('all');
                    setAdvancedFilters({ date_start: '', date_end: '', state: '', value_min: '', value_max: '' });
                    loadOrders(1, '', 'all', { date_start: '', date_end: '', state: '', value_min: '', value_max: '' });
                  }}
                  className="px-6 py-3 text-sm font-medium text-brand-blue bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                >
                  Clear Filters
                </button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium text-slate-900 mb-2">No orders awaiting shipment</h3>
                <p className="text-slate-600 mb-6">
                  All orders have been shipped! New orders will appear here automatically.
                </p>
                <button 
                  onClick={handleSyncOrders}
                  disabled={syncing}
                  className="px-6 py-3 text-sm font-medium text-white bg-brand-blue hover:bg-brand-blue-hover rounded-lg transition-colors disabled:opacity-50"
                >
                  {syncing ? 'Syncing...' : 'Sync Orders Now'}
                </button>
              </>
            )}
          </div>
        )}
        </div>

        {/* Pagination - Fixed Footer */}
        {!isLoading && orders.length > 0 && (
          <div className="flex-shrink-0 bg-white border-t border-slate-200 px-6 py-3 flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Showing <span className="font-medium">{(page - 1) * ordersPerPage + 1}</span> to{' '}
              <span className="font-medium">{Math.min(page * ordersPerPage, totalOrders)}</span> of{' '}
              <span className="font-medium">{totalOrders}</span> orders
            </p>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-slate-600 mr-2">
                Page {page} of {Math.ceil(totalOrders / ordersPerPage)}
              </span>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= Math.ceil(totalOrders / ordersPerPage)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <OrderDetailsModal
          key={`modal-${selectedOrder.order?.id}`}
          order={{ ...selectedOrder.order, items: selectedOrder.items }}
          onClose={() => setSelectedOrder(null)}
          onShip={handleShipOrder}
          initialTab={initialTab}
        />
      )}

      {/* New Order Modal */}
      <NewOrderModal
        isOpen={showNewOrderModal}
        onClose={() => setShowNewOrderModal(false)}
        onOrderCreated={() => {
          loadOrders(page, searchQuery, deliveryFilter, advancedFilters);
        }}
      />
    </div>
  );
};
